import { useState, useCallback, useMemo } from 'react';
import * as R from 'ramda';
import { AlternatePayee, AlternatePayeeList, SelectOptionList, UseAlternatePayeeManagerProps } from '../../../../types'
import { useTransitionView } from '../useTransitionView';
import { createFormStrategy } from '../../strategies/formStrategies';
import { createFetchingStrategy } from '../../strategies/fetchingStrategies';
import useAlternatePayee from '../useAlternatePayee';
import DefaultSpinner from '../../DefaultSpinner';
import { sanitizeBody } from '../../Utils'
import { useTranslations } from '../useTranslations'
import Validator from '../../Validator'

const INITIAL_ALTERNATE_PAYEE: AlternatePayee = {
  id: 0,
  name: '',
  paymentMethodId: null,
  paymentCurrency: '',
  accountType: '',
  routingNumber: '',
  accountNumber: '',
  payableTo: '',
  finInstitutionName: '',
  altairVendorId: '',
  lastModified: ''
};

const bankCountryOptions: SelectOptionList = [
  { label: 'UNITED STATES', value: 33 }
];

const paymentCurrencyOptions: SelectOptionList = [
  { label: 'USD', value: 'USD' }
];

const accountTypeOptions: SelectOptionList = [
  { label: 'Checking', value: 'CHECKING' },
  { label: 'Savings', value: 'SAVINGS' }
];

export const useAlternatePayeeManager = (
  {
    restApi,
    user,
    showForbiddenErrorPage,
    apiConfig,
    services,
    translations: translationsOverride
  }: UseAlternatePayeeManagerProps) => {

  const alternatePayeeProps = {
    restApi,
    user,
    showForbiddenErrorPage,
    apiConfig,
    services
  }

  const [alternatePayeeData, fetcher, isLoading] = useAlternatePayee(alternatePayeeProps);
  const viewMode = useTransitionView();
  const [selectedDefaultValues, setSelectedDefaultValues] = useState<AlternatePayee>(INITIAL_ALTERNATE_PAYEE);

  // Translations
  const translations = useTranslations();


  // SummaryPage logic
  const showPayeeForm = useCallback(() => {
    setSelectedDefaultValues(INITIAL_ALTERNATE_PAYEE);
    viewMode.setCreating();
  }, [viewMode]);

  // is (id === 0) then []
  const validAlternatePayeeData = useMemo(() => {
    return alternatePayeeData[0]?.id === 0 ? [] : alternatePayeeData;
  }, [alternatePayeeData]);

  const handleView = useCallback((data: AlternatePayee) => {
    setSelectedDefaultValues(data);
    viewMode.setViewing();
  }, [viewMode]);

  // AlternatePayeeForm logic
  const handleEdit = useCallback(() => {
    viewMode.setEditing();
  }, [viewMode]);

  const handleSubmit = useCallback(async (data: AlternatePayee) => {
    const type = data.id > 0 ? 'PUT' : 'POST';
    const doFetch = 'PUT'.includes(type) ? fetcher.update : fetcher.save;
    const dataSanitized = sanitizeBody(data, type)
    const body: string = JSON.stringify(dataSanitized);
    await doFetch(body);
    viewMode.setIdle();
  }, [fetcher, viewMode]);

  const formStrategy = useMemo(() => {
    if (!viewMode.isPayeeForm) return null;

    return createFormStrategy(viewMode.mode, {
      handleSubmit,
      handleEdit,
      handleCancel: viewMode.setIdle,
    });
  }, [viewMode.mode, viewMode.isPayeeForm, handleSubmit, handleEdit, viewMode]);

  const fetchingStrategy = useMemo(
    () => createFetchingStrategy(isLoading, DefaultSpinner),
    [isLoading]
  );

  const validator = useMemo(() => new Validator(), []);

  return {
    // States
    alternatePayeeData: validAlternatePayeeData,
    selectedDefaultValues,
    bankCountryOptions,
    paymentCurrencyOptions,
    accountTypeOptions,
    viewMode,

    // Strategies
    formStrategy,
    fetchingStrategy,

    // Actions
    showPayeeForm,
    handleView,
    handleEdit,
    handleSubmit,

    // Translations
    translations,

    // Validator
    validator
  };
};
